/**
 * Theme Profile System
 * Manages theme profiles and applies CSS variables at runtime
 *
 * Four profiles ship. `light` and `dark` carry the IVYX palettes, which are
 * derived from ONE lightness ladder: both themes aim at the same target table
 * (below) and differ only in which direction they walk away from their canvas.
 * `classic-light` and `classic-dark` are the palettes that shipped up to
 * shell-classic 1.9.0, frozen byte-for-byte as the rollback path.
 *
 * WHY the ids are distributed this way round. Seven extensions read the profile
 * id with a string equality — `profile === 'light' ? 'vs' : 'vs-dark'`, in
 * extension-code-editor, -database, -ipynb-viewer, -edu-notebook, -duckdb,
 * -llms-instruction-class-service and -markdown-viewer — so a NEW id for the
 * new light palette would drop all seven into the else branch and put a dark
 * Monaco inside a light application. Keeping `light`/`dark` on the values
 * everyone gets by default also keeps a user's saved `theme.profile` working.
 * The cost is that Monaco stays dark under `classic-light`; that is written
 * down rather than fixed here, because fixing it means editing seven
 * extensions, and the fix is to read a mode rather than an id.
 *
 * THE TARGET TABLE (contrast against the profile's own `--background-main`,
 * WCAG 2.x relative luminance). `check:theme-contrast` holds every row:
 *
 *   surface step (paper vs main)   dL* >= 9, and within 25% between the themes
 *   --text-primary                 >= 14:1
 *   --text-secondary               6.6 - 8.0:1
 *   --text-placeholder             >= 4.5:1      (classic light was 3.04:1)
 *   --disabled                     2.2 - 2.6:1   (classic used ONE value for
 *                                                 both themes: 12.27:1 in
 *                                                 light, 1.50:1 in dark)
 *   --text-on-disabled             2.6 - 3.2:1   measured against --disabled,
 *                                                 NOT against the canvas:
 *                                                 `punica-input` and
 *                                                 `punica-select` paint that
 *                                                 token as the FILL of a
 *                                                 disabled field. The row
 *                                                 exists because the text on
 *                                                 that fill was a hardcoded
 *                                                 #a0a0a0 — 2.93:1 in dark,
 *                                                 1.13:1 in light.
 *   <status>-main                  light 5.4:1, dark 6.5:1
 *   <status>-light                 a subtle fill: 1.25 - 1.35:1, and
 *                                  --text-primary on it >= 10:1
 *   <status>-dark                  a strong fill: white on it >= 8:1
 *   --divider                      1.6-1.8:1     (the structural seam, which
 *                                                 runs BETWEEN the two
 *                                                 surfaces, so it is measured
 *                                                 against the canvas. It was
 *                                                 solved at 2.0 first and read
 *                                                 as too heavy in the product:
 *                                                 the same value also draws
 *                                                 every row rule INSIDE a card,
 *                                                 where 2.0 on the canvas is
 *                                                 1.56 on the card and shouts.
 *                                                 1.7 keeps the seam well above
 *                                                 classic's 1.43 — the value
 *                                                 that made edges vanish — and
 *                                                 takes the inner rule to 1.32)
 *   --background-navbar            white on it >= 4.5:1
 *
 * The neutral ramp and --action-hover are measured against --background-paper
 * instead, because that is the surface they actually sit on: the activity bar,
 * the status bar and `.punica-card` are all painted with it. Measuring them
 * against the canvas is how the classic light theme ended up with an activity
 * bar hover at 1.07:1 against its own chrome, under a comment claiming hover
 * and active were separate signals.
 *
 *   --action-hover                 ~1.18:1  vs paper   (hover)
 *   --neutral-light                ~1.22:1  vs paper   (faintest rule)
 *   --neutral-main                 ~1.45:1  vs paper   (selected fill)
 *   --neutral-dark                 ~2.20:1  vs paper   (strongest neutral)
 *
 * The ramp is monotonic here — light, then main, then dark — which is what the
 * names say and what the classic profiles do NOT do (classic light has
 * `--neutral-light: #d1d9e0` sitting darker than `--neutral-main: #e9e9e9`).
 * The inversion is preserved in the frozen profiles and not carried forward.
 *
 * `--highlight` is the one token with four consumer roles at once, and one
 * value satisfies all four in BOTH themes — link text on the light canvas
 * (extension-viewer), a ring on the light chrome (extension-file-explorer), a
 * ring on the dark chrome, and a fill carrying `--text-on-highlight`
 * (extension-command-palette). The window is narrow, so the check asserts the
 * four roles separately rather than one number.
 */

import disabledFieldCss from './disabledField.css';

export interface ThemeProfile {
  id: string;
  name: string;
  variables: Record<string, string>;
}

export const THEME_PROFILES: Record<string, ThemeProfile> = {
  light: {
    id: 'light',
    name: 'IVYX Light',
    variables: {
      '--background-main': '#ffffff',
      '--background-paper': '#dee3e9',
      // The brand blue is the same value in both themes, and it is the SHIPPED
      // hue and saturation (hsl 214.8, 98.4%) taken down 3.5 points of
      // lightness — the smallest move that puts white text on it over the
      // 4.5:1 AA floor. The classic profiles' #086ffd measured 4.47:1 and
      // their own comments accepted that as a brand call; the identity is
      // unchanged here, only the readability.
      '--background-navbar': '#0266f1',
      '--background-tabbar': '#d0d7e0',
      '--background-backdrop': 'rgba(0, 0, 0, 0.32)',

      '--text-primary': '#252c35',
      '--text-secondary': '#515c69',
      '--text-placeholder': '#687584',
      '--text-navbar': '#ffffff',
      '--text-tabbar': '#0968ce',
      // Two consumers, and they disagreed about what was broken. capability.css
      // asked for `--text-link` with no profile answering, so its links
      // rendered the literal #6ea8fe — a dark-theme blue at 1.14:1 here. And
      // `createLink`, the shell helper nine extensions build links with, set no
      // colour at all, so those rendered the user agent's #0000EE — 1.47:1 on
      // the dark chrome. Both read this token now.
      //
      // Solved against `--background-paper` rather than the canvas: a link sits
      // on a card as often as on the page, and the card is the harder surface.
      // Against the canvas it comes out at 5.94:1.
      '--text-link': '#0862c2',
      // Declared because `--highlight` is a fill and nothing chose the text on
      // it: extension-command-palette's selected row inherited `--text-primary`.
      '--text-on-highlight': '#ffffff',

      '--icon-navbar': '#ffffff',
      '--icon-tabbar-active': '#0968ce',
      '--icon-tabbar-inactive': '#687584',

      // `-light` is a subtle fill on this theme's canvas, `-main` the colour
      // itself, `-dark` a strong fill that carries white text. The `-dark`
      // values are identical in both themes on purpose: "a fill white text
      // reads on at 8:1" does not depend on what is behind it.
      //
      // `--primary-light` is the exception, and it is a consumer that decides
      // it rather than a preference. punica-button's own shadow style carries
      // `:host([color='primary']):host([variant='text']):host(:hover) {
      // background-color: var(--primary-light) }`, and every activity-bar item
      // is exactly that button — so this token is an interactive tint painted
      // over `--background-paper`, not a status fill on the canvas. Solved at
      // ~1.9:1 against the chrome. A canvas-subtle value measured 1.01:1 there
      // and made the hover disappear; only driving the product showed it.
      '--primary-light': '#7fa7df',
      '--primary-main': '#0262e5',
      '--primary-dark': '#014aaf',

      '--error-light': '#fadcde',
      '--error-main': '#cf1f2b',
      '--error-dark': '#9e1821',

      '--warning-light': '#fcdfb8',
      '--warning-main': '#995c06',
      '--warning-dark': '#744605',

      '--success-light': '#b8efcd',
      '--success-main': '#1b793d',
      '--success-dark': '#145c2f',

      '--info-light': '#c4e9fb',
      '--info-main': '#0c70a2',
      '--info-dark': '#09557b',

      '--neutral-light': '#c7ced8',
      // The selected-row fill. It is a separate step from `--action-hover`
      // now: `card.css` used to paint `:hover` and `--selected` with this one
      // token, so a selected card and a hovered card were the same pixels.
      '--neutral-main': '#b4becb',
      '--neutral-dark': '#8c9aac',

      '--disabled': '#a4abb5',
      // `--disabled` is a TEXT token here (the band above) and two vendor
      // components also paint it as the FILL of a disabled field, under a
      // hardcoded `color: #a0a0a0`. That literal reads at 2.93:1 on dark's
      // fill and 1.13:1 on this one, so IVYX Light drew every disabled input
      // and select as a grey slab with text the colour of the slab. This is
      // the text on that fill, declared for the same reason as
      // `--text-on-highlight`, and held at the ratio dark already renders.
      // Same literal as `--text-secondary` today and a separate declaration on
      // purpose: it is measured against the fill, not against the canvas, so
      // the two move independently. See view/disabledField.css.
      '--text-on-disabled': '#515c69',
      '--highlight': '#0d7ac7',
      // Declared because capability.css already read it and no profile
      // answered, so every hover there rendered a fixed rgba(127,127,127,.08).
      // The hosts' activity bar moved onto it too — see the ramp note above.
      '--action-hover': '#cad2dc',

      '--white': '#ffffff',
      '--black': '#000000',

      '--divider': '#bfc7d2',
      '--focus-ring': '#0968ce',

      '--root-border-radius': '8px',
      '--root-padding': '16px'
    }
  },
  dark: {
    id: 'dark',
    name: 'IVYX Dark',
    variables: {
      '--background-main': '#131416',
      '--background-paper': '#2a2d33',
      // Same value as light, on purpose: one brand blue across both themes.
      '--background-navbar': '#0266f1',
      '--background-tabbar': '#24272c',
      '--background-backdrop': 'rgba(0, 0, 0, 0.55)',

      '--text-primary': '#e5e9ed',
      '--text-secondary': '#9da8b4',
      '--text-placeholder': '#818993',
      '--text-navbar': '#ffffff',
      '--text-tabbar': '#499df5',
      '--text-link': '#4098f5',
      '--text-on-highlight': '#ffffff',

      '--icon-navbar': '#ffffff',
      '--icon-tabbar-active': '#499df5',
      '--icon-tabbar-inactive': '#818993',

      // `-light` is a DARK tint here, because its job is a subtle fill on this
      // theme's canvas. The classic dark profile shipped Bootstrap's #f8d7da
      // for `--error-light`, a near-white pink — extension-test paints a
      // failing row with it, so a failure read as a white block in a dark app.
      // `--primary-light` is the exception the light profile explains: it is
      // punica-button's text-variant hover, painted over the chrome.
      '--primary-light': '#265799',
      '--primary-main': '#529afe',
      '--primary-dark': '#014aaf',

      '--error-light': '#51171b',
      '--error-main': '#eb767e',
      '--error-dark': '#9e1821',

      '--warning-light': '#392810',
      '--warning-main': '#dd8509',
      '--warning-dark': '#744605',

      '--success-light': '#0e321b',
      '--success-main': '#27b059',
      '--success-dark': '#145c2f',

      '--info-light': '#122e3c',
      '--info-main': '#12a3eb',
      '--info-dark': '#09557c',

      '--neutral-light': '#363b41',
      '--neutral-main': '#40464f',
      '--neutral-dark': '#59616c',

      '--disabled': '#4e545b',
      // The literal `punica-input` and `punica-select` hardcode. Declared so
      // the token exists in every profile; this theme's rendering is unchanged.
      '--text-on-disabled': '#a0a0a0',
      '--highlight': '#0d7ac7',
      '--action-hover': '#34393f',

      '--white': '#ffffff',
      '--black': '#000000',

      '--divider': '#373e46',
      '--focus-ring': '#499df5',

      '--root-border-radius': '8px',
      '--root-padding': '16px'
    }
  },
  // ---------------------------------------------------------------------------
  // The two palettes that shipped up to 1.9.0, frozen. Their job is to be a
  // rollback, so `check:theme-contrast` compares them against these literals
  // and exempts them from every target — a value that changes here is a
  // regression even when it would measure better.
  //
  // The three tokens the IVYX profiles added are declared here too — a profile
  // that declares fewer keys than its siblings is a panel that loses its colour
  // on a rollback. Two of them carry the literal they already rendered from a
  // `var(--x, fallback)`, so Classic reproduces 1.9.0 there exactly.
  //
  // `--text-link` deliberately does NOT. It had two different prior renderings
  // — #6ea8fe from capability.css's fallback and the user agent's #0000EE from
  // `createLink` — so no single literal restores both, and freezing it to the
  // first took the llama.cpp link in Settings from 8.25:1 to 2.12:1. A token
  // that did not exist in 1.9.0 has no pixel to restore; its value here is a
  // choice, and the choice is readability on this profile's own surfaces.
  // ---------------------------------------------------------------------------
  'classic-light': {
    id: 'classic-light',
    name: 'Classic Light',
    variables: {
      '--background-main': '#ffffff',
      '--background-paper': '#f0f0f0',
      '--background-navbar': '#086ffd',
      '--background-tabbar': '#e1e4e8',
      '--background-backdrop': 'rgba(0, 0, 0, 0.25)',

      '--text-primary': '#24292f',
      '--text-secondary': '#57606a',
      '--text-placeholder': '#8c959f',
      '--text-navbar': '#ffffff',
      '--text-tabbar': '#0969da',
      '--text-link': '#096ad2',
      '--text-on-highlight': '#24292f',

      '--icon-navbar': '#ffffff',
      '--icon-tabbar-active': '#0969da',
      '--icon-tabbar-inactive': '#656d76',

      '--primary-light': '#58a6ff',
      '--primary-main': '#0969da',
      '--primary-dark': '#0550ae',

      '--error-light': '#ffebe9',
      '--error-main': '#cf222e',
      '--error-dark': '#a40e26',

      '--warning-light': '#fff8c5',
      '--warning-main': '#9a6700',
      '--warning-dark': '#7d4e00',

      '--success-light': '#dafbe1',
      '--success-main': '#1a7f37',
      '--success-dark': '#116329',

      '--info-light': '#ddf4ff',
      '--info-main': '#0969da',
      '--info-dark': '#0550ae',

      '--neutral-light': '#d1d9e0',
      '--neutral-main': '#e9e9e9',
      '--neutral-dark': '#8c959f',

      '--disabled': '#33353a',
      // Frozen at the literal it already rendered — see FROZEN_ADDED in
      // scripts/themeContrast.mjs.
      '--text-on-disabled': '#a0a0a0',
      '--highlight': '#ddf4ff',
      '--action-hover': 'rgba(127, 127, 127, 0.08)',

      '--white': '#ffffff',
      '--black': '#000000',

      '--divider': '#d1d9e0',
      '--focus-ring': '#0969da',

      '--root-border-radius': '8px',
      '--root-padding': '16px'
    }
  },
  'classic-dark': {
    id: 'classic-dark',
    name: 'Classic Dark',
    variables: {
      '--background-main': '#131416',
      '--background-paper': '#2a2d33',
      '--background-navbar': '#086ffd',
      '--background-tabbar': '#232529',
      '--background-backdrop': 'rgba(0, 0, 0, 0.5)',

      '--text-primary': '#f5f7fa',
      '--text-secondary': '#c0c4cc',
      '--text-placeholder': '#9aa0a6',
      '--text-navbar': '#ffffff',
      '--text-tabbar': '#0a84ff',
      '--text-link': '#4098f5',
      '--text-on-highlight': '#f5f7fa',

      '--icon-navbar': '#ffffff',
      '--icon-tabbar-active': '#0a84ff',
      '--icon-tabbar-inactive': '#6b6e76',

      '--primary-light': '#66b3ff',
      '--primary-main': '#0a84ff',
      '--primary-dark': '#0056b3',

      '--error-light': '#f8d7da',
      '--error-main': '#ff453a',
      '--error-dark': '#c41c12',

      '--warning-light': '#ffe28a',
      '--warning-main': '#ffd60a',
      '--warning-dark': '#c9a400',

      '--success-light': '#48e09e',
      '--success-main': '#30d158',
      '--success-dark': '#228c3f',

      '--info-light': '#4ac8ff',
      '--info-main': '#64d2ff',
      '--info-dark': '#0a84ff',

      '--neutral-light': '#45484e',
      '--neutral-main': '#35383f',
      '--neutral-dark': '#25272b',

      '--disabled': '#33353a',
      // Frozen at the literal it already rendered.
      '--text-on-disabled': '#a0a0a0',
      '--highlight': '#52a8ff',
      '--action-hover': 'rgba(127, 127, 127, 0.08)',

      '--white': '#ffffff',
      '--black': '#000000',

      '--divider': '#3a3d44',
      '--focus-ring': '#52a8ff',

      '--root-border-radius': '8px',
      '--root-padding': '16px'
    }
  }
};

/**
 * The sentinel `loadProfile` accepts instead of a profile id, meaning "follow
 * the OS". It is deliberately NOT a fifth entry in THEME_PROFILES: `getProfiles`
 * must keep listing real palettes, and `getCurrentProfile` must keep returning a
 * real id — seven extensions branch on `profile === 'light'` and would put a
 * dark Monaco inside a light application the moment they saw anything else.
 *
 * So the sentinel is a stored PREFERENCE, and what the manager reports is always
 * the RESOLVED profile. A caller that needs to know which of the two it is reads
 * the setting: `settings.manager.get('system', 'theme.profile')`.
 */
export const SYSTEM_PROFILE = 'system';

const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

/**
 * The host's own "the OS appearance changed" signal.
 *
 * `matchMedia(...).addEventListener('change')` is the obvious mechanism and it
 * DOES NOT FIRE in an Electron renderer: measured on Electron 1.0.22 against a
 * real macOS appearance flip, `matches` updates correctly while `addEventListener`,
 * `addListener` and `onchange` all stayed at zero calls. So the query is usable
 * for READING the current state and not for being told it changed.
 *
 * The host therefore pushes one: Electron's main process watches
 * `nativeTheme.on('updated')` and the preload dispatches this event on `window`.
 * A CustomEvent rather than a new manager method keeps it out of the substrate's
 * type declaration, and a host that has no such signal simply never fires it —
 * the matchMedia listener below is still attached for hosts where it works
 * (an ordinary browser tab does fire it).
 */
export const SYSTEM_THEME_EVENT = 'punica:system-theme-changed';

/**
 * Announced on `window` every time a profile is applied, carrying the id and
 * the canvas colour, so a host can remember what to paint before the next run's
 * renderer exists.
 *
 * This does NOT go through the settings manager, even though that is where the
 * preference lives. The manager's Electron branch keys on
 * `window.api.getPath('userData')` and **this host does not expose `getPath`**,
 * so it falls through to the browser branch and keeps global settings in
 * localStorage — which the main process cannot read. Rather than migrate every
 * existing user's settings to make one boot hint readable, the hint takes its
 * own one-way channel and the host owns the file.
 */
export const THEME_APPLIED_EVENT = 'punica:theme-applied';

const DISABLED_FIELD_STYLE_ID = 'punica-disabled-field-styles';

/**
 * Put the disabled-field text rule in the document, once.
 *
 * It rides with the theme rather than with `createInput`/`createSelect`,
 * because the defect is in the COMPONENT and not in the helper: markup that
 * writes `<punica-input disabled>` by hand has it too, and 21 call sites across
 * the fleet build selects without going through a helper at all. Every host
 * paints a profile at boot, so hanging it off the paint reaches all of them.
 *
 * Idempotent by id, and a no-op where there is no document (the unit suite
 * imports this module for the palettes alone).
 */
export function ensureDisabledFieldStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(DISABLED_FIELD_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = DISABLED_FIELD_STYLE_ID;
  style.textContent = disabledFieldCss;
  document.head.appendChild(style);
}

/** Relative luminance, the WCAG definition. Used to classify a palette. */
function luminanceOf(hex: string): number | null {
  if (!/^#[0-9a-f]{6}$/i.test(hex)) return null;
  const channel = (v: number) =>
    v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  const [r, g, b] = [1, 3, 5].map((i) =>
    channel(parseInt(hex.slice(i, i + 2), 16) / 255)
  );
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Whether a profile is a dark one, asked of the palette rather than of a flag.
 *
 * A `mode: 'dark'` field would be a second, independent statement that can
 * disagree with the colours underneath it; the canvas cannot. It also means a
 * profile added through `registerProfile` takes part in the system pairing
 * without declaring anything.
 */
export function isDarkProfile(profile: ThemeProfile): boolean | null {
  const y = luminanceOf(profile.variables['--background-main'] ?? '');
  return y === null ? null : y < 0.18;
}

/**
 * The profile to switch to when the OS flips, for a given one.
 *
 * The pairing stays inside the family: Classic Dark answers Classic Light, not
 * IVYX Light. Family is the id with its `light`/`dark` word removed, so
 * `classic-dark` and `classic-light` pair, and `dark`/`light` pair. A profile
 * with no counterpart returns null and the caller leaves it alone — System must
 * not silently move somebody off the palette they chose.
 */
export function counterpartOf(
  profileId: string,
  profiles: Record<string, ThemeProfile> = THEME_PROFILES
): string | null {
  const self = profiles[profileId];
  if (!self) return null;
  const selfDark = isDarkProfile(self);
  if (selfDark === null) return null;

  const familyOf = (id: string) =>
    id.replace(/(^|-)(light|dark)$/, '').replace(/-$/, '');
  const family = familyOf(profileId);

  for (const [id, profile] of Object.entries(profiles)) {
    if (id === profileId) continue;
    if (familyOf(id) !== family) continue;
    const dark = isDarkProfile(profile);
    if (dark !== null && dark !== selfDark) return id;
  }
  return null;
}

class ThemeManager {
  private currentProfileId: string | null = null;
  private listeners: Set<(profileId: string) => void> = new Set();

  /** The stored preference, which may be SYSTEM_PROFILE. */
  private preference: string | null = null;
  /** Which profile the system pairing swings around. */
  private systemAnchor: string = 'dark';
  private mediaQuery: MediaQueryList | null = null;
  private mediaListener: (() => void) | null = null;
  private hostSchemeListener: (() => void) | null = null;

  /** What the OS is asking for right now, or null where it cannot be read. */
  private prefersDark(): boolean | null {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
      return null;
    try {
      return window.matchMedia(DARK_SCHEME_QUERY).matches;
    } catch {
      return null;
    }
  }

  /**
   * Turn the stored preference into a profile that exists. `system` resolves
   * against the OS; anything else is itself.
   */
  private resolve(preference: string): string {
    if (preference !== SYSTEM_PROFILE) return preference;

    const wantDark = this.prefersDark();
    const anchor = THEME_PROFILES[this.systemAnchor]
      ? this.systemAnchor
      : 'dark';
    if (wantDark === null) return anchor;

    const anchorProfile = THEME_PROFILES[anchor];
    if (!anchorProfile) return 'dark';
    const anchorIsDark = isDarkProfile(anchorProfile);
    if (anchorIsDark === null || anchorIsDark === wantDark) return anchor;

    // The anchor is the wrong side of the OS setting — swing to its counterpart,
    // and stay on the anchor if the family has none rather than leaving it.
    return counterpartOf(anchor) ?? anchor;
  }

  /** Attached only while the preference is `system`. */
  private watchSystem(): void {
    if (typeof window === 'undefined') return;

    // The host's push signal. This is the one that actually fires in Electron.
    if (!this.hostSchemeListener && typeof window.addEventListener === 'function') {
      const onHost = () => {
        if (this.preference !== SYSTEM_PROFILE) return;
        this.applyResolved(this.resolve(SYSTEM_PROFILE));
        void this.persist(SYSTEM_PROFILE, this.currentProfileId ?? '');
      };
      window.addEventListener(SYSTEM_THEME_EVENT, onHost);
      this.hostSchemeListener = onHost;
    }

    if (typeof window.matchMedia !== 'function') return;
    if (this.mediaQuery) return;
    try {
      const mq = window.matchMedia(DARK_SCHEME_QUERY);
      const listener = () => {
        if (this.preference !== SYSTEM_PROFILE) return;
        void this.applyResolved(this.resolve(SYSTEM_PROFILE));
      };
      // `addEventListener` is the current API; `addListener` is the fallback
      // for the older MediaQueryList some environments still hand back.
      if (typeof mq.addEventListener === 'function') {
        mq.addEventListener('change', listener);
      } else if (typeof (mq as unknown as { addListener?: unknown }).addListener === 'function') {
        (
          mq as unknown as { addListener: (l: () => void) => void }
        ).addListener(listener);
      }
      this.mediaQuery = mq;
      this.mediaListener = listener;
    } catch (err) {
      console.error('[Theme] Could not watch the system colour scheme:', err);
    }
  }

  private unwatchSystem(): void {
    if (this.hostSchemeListener && typeof window !== 'undefined') {
      window.removeEventListener(SYSTEM_THEME_EVENT, this.hostSchemeListener);
      this.hostSchemeListener = null;
    }

    const mq = this.mediaQuery;
    const listener = this.mediaListener;
    if (!mq || !listener) return;
    try {
      if (typeof mq.removeEventListener === 'function') {
        mq.removeEventListener('change', listener);
      } else if (typeof (mq as unknown as { removeListener?: unknown }).removeListener === 'function') {
        (
          mq as unknown as { removeListener: (l: () => void) => void }
        ).removeListener(listener);
      }
    } catch {
      /* leaving a listener attached is better than throwing on teardown */
    }
    this.mediaQuery = null;
    this.mediaListener = null;
  }

  /** Paint a profile and tell everyone. Does NOT touch the stored preference. */
  private applyResolved(profileId: string): void {
    const profile = THEME_PROFILES[profileId];
    if (!profile) {
      console.warn(`[Theme] Profile not found: ${profileId}`);
      return;
    }

    const root = document.documentElement;
    Object.entries(profile.variables).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
    ensureDisabledFieldStyles();

    this.currentProfileId = profileId;
    this.listeners.forEach((listener) => listener(profileId));
  }

  /**
   * Record the preference, and announce what it resolved to.
   *
   * Two different destinations on purpose: the preference is a user setting and
   * goes through the settings manager; the resolved profile and its canvas are a
   * hint for the HOST (Electron paints its window before any renderer exists)
   * and go out as an event the host persists itself. See THEME_APPLIED_EVENT for
   * why they cannot share the settings file.
   */
  private announce(resolvedId: string, following: boolean): void {
    if (typeof window === 'undefined' || typeof CustomEvent !== 'function') return;
    const canvas = THEME_PROFILES[resolvedId]?.variables['--background-main'];
    if (!canvas) return;
    try {
      window.dispatchEvent(
        new CustomEvent(THEME_APPLIED_EVENT, {
          // `following` is the half a host cannot work out for itself, and it
          // decides whether the host may correct the hint against the OS at
          // boot. Correcting a PINNED profile would open a light user dark
          // whenever their desktop was dark.
          detail: { profile: resolvedId, canvas, following }
        })
      );
    } catch {
      /* a host with no listener is the ordinary case */
    }
  }

  private async persist(preference: string, resolvedId: string): Promise<void> {
    this.announce(resolvedId, preference === SYSTEM_PROFILE);
    if (typeof punica === 'undefined' || !punica.extensions?.settings?.manager)
      return;
    try {
      await punica.extensions.settings.manager.set(
        'system',
        'theme.profile',
        preference,
        'global'
      );

    } catch (err) {
      console.error('[Theme] Failed to save profile to settings:', err);
    }
  }

  /**
   * Load a theme profile by ID, or `system` to follow the OS.
   */
  async loadProfile(profileId: string): Promise<void> {
    if (profileId !== SYSTEM_PROFILE && !THEME_PROFILES[profileId]) {
      console.warn(`[Theme] Profile not found: ${profileId}`);
      return;
    }

    if (profileId === SYSTEM_PROFILE) {
      // Swing around whatever is on screen, so turning System on from Classic
      // Dark lands on Classic Light rather than on another family entirely.
      if (this.currentProfileId && THEME_PROFILES[this.currentProfileId]) {
        this.systemAnchor = this.currentProfileId;
      }
      this.preference = SYSTEM_PROFILE;
      this.watchSystem();
    } else {
      this.preference = profileId;
      this.systemAnchor = profileId;
      this.unwatchSystem();
    }

    const resolved = this.resolve(this.preference);
    this.applyResolved(resolved);
    await this.persist(this.preference, resolved);
  }

  /**
   * The profile currently on screen. Never `system` — see SYSTEM_PROFILE.
   */
  getCurrentProfile(): string | null {
    return this.currentProfileId;
  }

  /**
   * What the user chose, which may be `system`. `getCurrentProfile` says what
   * that resolved to.
   */
  getPreference(): string | null {
    return this.preference;
  }

  /** Whether the OS is currently asking for dark, or null where unreadable. */
  getSystemPrefersDark(): boolean | null {
    return this.prefersDark();
  }

  /**
   * Get all available profiles
   */
  getProfiles(): Record<string, ThemeProfile> {
    return { ...THEME_PROFILES };
  }

  /**
   * Get a specific profile
   */
  getProfile(profileId: string): ThemeProfile | undefined {
    return THEME_PROFILES[profileId];
  }

  /** The profile System would swap this one for, or null if it has no pair. */
  getCounterpart(profileId: string): string | null {
    return counterpartOf(profileId);
  }

  /**
   * Register a custom profile (for future extension)
   */
  registerProfile(profile: ThemeProfile): void {
    THEME_PROFILES[profile.id] = profile;
  }

  /**
   * Subscribe to profile changes
   */
  onDidChangeProfile(callback: (profileId: string) => void): () => void {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Load a profile immediately (synchronously) without checking settings.
   *
   * Called before anything async so the CSS variables exist at first paint. The
   * host passes what it already resolved at window creation; with no argument
   * this keeps its old behaviour and applies `dark`, which is what the browser
   * host does.
   */
  loadDefaultImmediate(preference?: string, anchor?: string): void {
    if (anchor && THEME_PROFILES[anchor]) this.systemAnchor = anchor;

    // `system` is resolved right here rather than deferred: matchMedia answers
    // synchronously in a renderer, so the first paint can already be the right
    // side. Passing the raw preference through and letting it fall back to
    // `dark` would put a dark frame in front of a light user — the defect this
    // argument exists to close.
    const wanted =
      preference === SYSTEM_PROFILE
        ? this.resolve(SYSTEM_PROFILE)
        : preference && THEME_PROFILES[preference]
          ? preference
          : 'dark';
    const profile = THEME_PROFILES[wanted];
    if (!profile) {
      console.warn('[Theme] Default dark profile not found');
      return;
    }

    const root = document.documentElement;
    Object.entries(profile.variables).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
    ensureDisabledFieldStyles();

    this.currentProfileId = wanted;
    if (!anchor || !THEME_PROFILES[anchor]) this.systemAnchor = wanted;
  }

  /**
   * Initialize theme from settings
   */
  async initialize(): Promise<void> {
    if (
      typeof punica === 'undefined' ||
      !punica.extensions?.settings?.manager
    ) {
      // Fallback to dark theme if settings not available
      await this.loadProfile('dark');
      return;
    }

    try {
      await punica.extensions.settings.manager.load();
      const saved = punica.extensions.settings.manager.get(
        'system',
        'theme.profile'
      );

      if (saved === SYSTEM_PROFILE) {
        // The anchor is whatever loadDefaultImmediate put on screen, so a
        // system user keeps their family across restarts.
        await this.loadProfile(SYSTEM_PROFILE);
      } else if (saved && typeof saved === 'string' && THEME_PROFILES[saved]) {
        await this.loadProfile(saved);
      } else {
        await this.loadProfile('dark'); // default
      }
    } catch (err) {
      console.error('[Theme] Failed to initialize from settings:', err);
      await this.loadProfile('dark'); // fallback
    }
  }
}

export const themeManager = new ThemeManager();
